import { OnboardingFlowGroup, type OnboardingGuidanceFlow, type OnboardingGuidanceStep } from '../../../types';

export const stepOpenTryItLive: OnboardingGuidanceStep = {
  title: 'Try it live',
  description:
    'Click Try it live to start a real conversation with both variations in the side panel.\n\n*Open Try it live to continue*',
  selector: 'try-it-live-button',
};

// Anchored to the drawer body (not the Close button that actually advances the step): anchoring to
// the button parked the popover on top of the chat content. With the full-height drawer as the
// trigger, the popover's only fit is to its left — beside the chat app, obscuring nothing. The
// drawer flips the package's top-right highlight dot (which would land in the dead screen corner)
// to its top-left — see TryItLiveDrawer.module.css.
export const stepChatWithAgents: OnboardingGuidanceStep = {
  title: 'Chat with your agents',
  description: 'Send a message in the panel to compare how both variations respond.\n\n*Close the panel to continue*',
  selector: 'try-it-live-drawer',
};

export const stepMakeAChange: OnboardingGuidanceStep = {
  title: 'Make a change',
  description: 'Edit the agent prompt, then click Review and save.\n\n*Review your changes to continue*',
  selector: 'instructions-field',
};

// Anchored to the Save changes button inside the review modal. Saving happens in two clicks
// (Review and save → confirm in the modal), so this step keeps the guidance pointed at the
// confirmation button instead of leaving the popover stranded on the variation card behind
// the modal. Cancelling the modal steps back to stepMakeAChange.
export const stepSaveChanges: OnboardingGuidanceStep = {
  title: 'Save your changes',
  description: 'Review the summary of your edits, then click Save changes.\n\n*Save your changes to continue*',
  selector: 'save-changes-button',
};

// Same selector as stepOpenTryItLive: both anchor to the Try it live button.
export const stepTryItAgain: OnboardingGuidanceStep = {
  title: 'Try it again',
  description:
    'Reopen Try it live. Your edits show up in the responses right away, with no redeploy.\n\n*Reopen Try it live to continue*',
  selector: 'try-it-live-button',
};

// Same selector as stepChatWithAgents on purpose: both anchor to the drawer body and wait for the
// user to close the panel (the drawer's Close handler advances whichever of the two is active).
export const stepSeeTheDifference: OnboardingGuidanceStep = {
  title: 'Compare the responses',
  description:
    'Your saved changes are already live. Compare the new responses with the ones from before.\n\n*Close the panel to continue*',
  selector: 'try-it-live-drawer',
};

// Variant of the final "Roll it out" step for users who haven't connected an SDK yet: when the
// starter chatbot banner is showing its "wire it up using our SDK" copy, its "Set up SDK" link
// carries this variant's anchor, so the tour ends by pointing there instead of at the Targeting
// tab, and leads with connecting the app. The link also carries an action-gate validation marker
// while this step is active — opening the SDK setup panel completes the tour (hence the "Open Set
// up SDK to finish" affordance line).
export const stepSetUpSdk: OnboardingGuidanceStep = {
  title: 'Roll it out',
  description:
    'Connect this config to your app, then use Targeting to control which users or environments get it. You can roll back safely if needed.\n\n*Open Set up SDK to finish*',
  selector: 'setup-sdk-link',
};

export const stepRollItOut: OnboardingGuidanceStep = {
  title: 'Roll it out',
  description:
    'Go to Targeting to control which users or environments get this config. You can roll back safely if needed.',
  selector: 'targeting-tab',
  variants: [stepSetUpSdk],
};

export const tryItLiveTour: OnboardingGuidanceFlow = {
  name: 'tryItLiveTour',
  description: 'Chat with your config live, make a change, and roll it out.',
  steps: [
    stepOpenTryItLive,
    stepChatWithAgents,
    stepMakeAChange,
    stepSaveChanges,
    stepTryItAgain,
    stepSeeTheDifference,
    stepRollItOut,
  ],
  group: OnboardingFlowGroup.AiConfigs,
};
